import { mediaManifest } from '../generated/media';
import { botanicalTour, kttuTours } from '../app/components/destinationCatalog';
import { coursePartner, courses, courseValues } from './courses';
import { faqByPath } from './faq';
import { isExcursionHidden, orderByExcursion } from './excursions';
import type { ExcursionFactsMap } from './excursions';
import type { Excursion } from './schemas';

type MediaName = keyof typeof mediaManifest;
type Crumb = { name: string; path: string };

export type SeoRoute = {
  path: string;
  /** Lazy page module (Vite manifest key) whose CSS/JS the server-rendered HTML must link. */
  module?: string;
  title: string;
  description: string;
  /** Social preview photo; defaults to the depot hero shot. */
  image?: MediaName;
  /** The page renders `image` eagerly above the fold with these `<picture sizes>`; the server preloads it. */
  preloadHero?: string;
  breadcrumbs: Crumb[];
  sitemap?: { priority: number; changefreq: 'weekly' | 'monthly' | 'yearly' };
  noindex?: boolean;
  kind: 'catalog' | 'trip' | 'page' | 'legal' | 'error';
  /** Tour page; its meeting place comes from the admin-editable excursion facts. */
  trip?: { name: string; excursion: Excursion };
};

export const site = {
  name: 'Изнанка',
  legalName: 'ИП Постовой Константин Андреевич',
  phone: '+7-908-693-59-09',
  city: 'Краснодар',
  region: 'Краснодарский край',
  sameAs: ['https://t.me/iznanka_krd', 'https://t.me/krd_iznanka'],
  locale: 'ru_RU',
  themeColor: '#f0e7d8',
} as const;

const home: Crumb = { name: 'Главная', path: '/' };
const kttu: Crumb = { name: 'МУП «КТТУ»', path: '/excursions/kttu' };
const tripPage = (path: string, name: string, excursion: Excursion, title: string, description: string, image?: MediaName): SeoRoute => ({
  path, module: 'src/app/pages/ExcursionPage.tsx', kind: 'trip', image, title, description,
  breadcrumbs: [home, kttu, { name, path }], sitemap: { priority: 0.8, changefreq: 'monthly' }, trip: { name, excursion },
});

export const seoRoutes: SeoRoute[] = [
  {
    path: '/', module: 'src/app/pages/HomePage.tsx', kind: 'catalog', image: 'IMG_0129.JPG', preloadHero: '(max-width: 850px) 46vw, 560px',
    title: 'Экскурсии в Краснодаре: депо, трамвай, ботаника — «Изнанка»',
    description: 'Экскурсии в Краснодаре для школьников, студентов и семей: Восточное и Западное трамвайные депо, троллейбусное депо МУП «КТТУ», трамвай по городу, караоке на колёсах и ботаническая прогулка. Оставьте заявку.',
    breadcrumbs: [home], sitemap: { priority: 1, changefreq: 'weekly' },
  },
  {
    path: '/excursions/vostochnoe-tramvaynoe-depo', module: 'src/app/pages/VtdPage.tsx', kind: 'trip', image: 'vtd-group.jpg', preloadHero: '100vw',
    title: 'Экскурсия в Восточное трамвайное депо Краснодара — «Изнанка»',
    description: 'Экскурсия в Восточное трамвайное депо (ул. Ставропольская, 202): веер путей, парк вагонов, место водителя, история трамвая Екатеринодара, поездка по территории и финальная викторина.',
    breadcrumbs: [home, kttu, { name: 'Восточное трамвайное депо', path: '/excursions/vostochnoe-tramvaynoe-depo' }],
    sitemap: { priority: 0.9, changefreq: 'monthly' },
    trip: { name: 'Экскурсия в Восточное трамвайное депо', excursion: 'eastern_tram_depot' },
  },
  tripPage('/excursions/tramvay-po-gorodu', 'Трамвай по городу', 'city_tram',
    'Экскурсия на трамвае по Краснодару — «Изнанка»',
    'Экскурсионная поездка на трамвае по Краснодару: знакомые улицы с другого ракурса, история города и его транспорта. Для групп и индивидуально, дату согласуем после заявки.', 'city-tram-custom.jpg'),
  tripPage('/excursions/pervoe-trolleybusnoe-depo', 'Первое троллейбусное депо', 'trolleybus_depot_1',
    'Экскурсия в Первое троллейбусное депо Краснодара — «Изнанка»',
    'Экскурсия в Первое троллейбусное депо МУП «КТТУ»: как троллейбусы готовят к рейсу, кто работает в депо и что скрыто от пассажиров. Для школьных классов, студентов и семей.', 'trolleybus-depot.jpg'),
  tripPage('/excursions/zapadnoe-tramvaynoe-depo', 'Западное трамвайное депо', 'western_tram_depot',
    'Экскурсия в Западное трамвайное депо Краснодара — «Изнанка»',
    'Экскурсия в Западное трамвайное депо МУП «КТТУ» (ул. Славянская, 89): пути и вагоны, работа депо и профессии, которые готовят трамвай к рейсу. Для школьных классов, студентов и семей.', 'IMG_0129.JPG'),
  tripPage('/excursions/tramvay-nestandartnyy-marshrut', 'Трамвай: нестандартный маршрут', 'city_tram_custom',
    'Трамвай по нестандартному маршруту в Краснодаре — «Изнанка»',
    'Поездка на трамвае по нестандартному маршруту: программу и маршрут собираем под интересы вашей группы вместе с организатором. Экскурсии «Изнанки» с МУП «КТТУ».', 'city-tram.jpg'),
  tripPage('/excursions/karaoke-na-kolesah', 'Караоке на колёсах', 'tram_karaoke',
    'Караоке-трамвай в Краснодаре: караоке на колёсах — «Изнанка»',
    'Караоке на колёсах — музыкальная поездка на трамвае по Краснодару для вашей компании: класса, коллег или семьи. Программу и детали согласуем заранее.', 'IMG_3086.JPG'),
  {
    path: '/excursions/kttu', module: 'src/app/pages/KttuPage.tsx', kind: 'catalog', image: 'IMG_3086.JPG',
    title: 'Экскурсии МУП «КТТУ» в Краснодаре: депо и трамвай — «Изнанка»',
    description: 'Шесть экскурсий с МУП «КТТУ»: Восточное и Западное трамвайные депо, Первое троллейбусное депо, трамвай по городу, нестандартный маршрут и караоке на колёсах. Для групп и индивидуально.',
    breadcrumbs: [home, kttu], sitemap: { priority: 0.9, changefreq: 'monthly' },
  },
  {
    path: '/excursions/botanicheskaya-progulka', module: 'src/app/pages/BotanicalPage.tsx', kind: 'trip',
    title: 'Ботаническая прогулка и гербарий в Краснодаре — «Изнанка»',
    description: 'Ботаническая прогулка в Краснодаре: Чистяковская роща, Солнечный остров, Ботанический сад или парк рядом с вами. Учимся различать растения и бережно собирать гербарий.',
    breadcrumbs: [home, { name: 'Ботаническая прогулка', path: '/excursions/botanicheskaya-progulka' }],
    sitemap: { priority: 0.8, changefreq: 'monthly' },
    trip: { name: 'Ботаническая прогулка и сбор гербария', excursion: 'botanical_walk' },
  },
  {
    path: '/shkolam', module: 'src/app/pages/SchoolsPage.tsx', kind: 'page', image: 'IMG_3086.JPG',
    title: 'Экскурсии для школьников в Краснодаре: профориентация — «Изнанка»',
    description: 'Экскурсии для школьных классов в Краснодаре: депо МУП «КТТУ», трамвай, ботаническая прогулка. Профориентация, инструктаж и безопасность, договор и счёт для школы.',
    breadcrumbs: [home, { name: 'Школам', path: '/shkolam' }], sitemap: { priority: 0.8, changefreq: 'monthly' },
  },
  {
    path: '/kursy', module: 'src/app/pages/CoursesPage.tsx', kind: 'page',
    title: 'Курсы по дронам и нейросетям для детей и взрослых в Краснодаре — «Изнанка»',
    description: 'Курсы и лекции школы «Добро и Небо»: первые полёты на дроне, FPV-дроны, агродроны, мультфильмы и 3D-модели с нейросетями для детей; оператор, механик и инструктор беспилотных систем для взрослых. Заявка через «Изнанку».',
    breadcrumbs: [home, { name: 'Курсы и лекции', path: '/kursy' }], sitemap: { priority: 0.8, changefreq: 'monthly' },
  },
  {
    path: '/partners', module: 'src/app/pages/PartnersPage.tsx', kind: 'page', image: 'IMG_0809.JPG',
    title: 'Промышленные экскурсии для предприятий Кубани — «Изнанка»',
    description: 'Промышленные экскурсии для детей и взрослых: профориентация, доверие к бренду, маршрут под ваше предприятие и продвижение через совместные публикации. Телефон: +7 (908) 693-59-09.',
    breadcrumbs: [home, { name: 'Партнёрам', path: '/partners' }], sitemap: { priority: 0.7, changefreq: 'monthly' },
  },
  {
    path: '/osnovatel', module: 'src/app/pages/FounderPage.tsx', kind: 'page',
    title: 'Константин Постовой — основатель «Изнанки», экскурсовод',
    description: 'Основатель агентства экскурсий «Изнанка» Константин Постовой: автор 25 маршрутов по Кубани, работал в департаменте транспорта, член профсоюза работников жизнеобеспечения и молодёжного совета.',
    breadcrumbs: [home, { name: 'Изнанка в лицах', path: '/#guides' }, { name: 'Константин Постовой', path: '/osnovatel' }],
    sitemap: { priority: 0.6, changefreq: 'monthly' },
  },
  {
    path: '/rozygrysh', module: 'src/app/pages/GiveawayPage.tsx', kind: 'page', image: 'IMG_3086.JPG',
    title: 'Конкурс: поездка на караоке-трамвае в Краснодаре — «Изнанка»',
    description: 'В честь запуска сайта «Изнанка» проводит конкурс с поездкой на трамвае «Караоке на колёсах» по Краснодару. Участие бесплатное: заполните форму и подпишитесь на канал «Изнанки» в Telegram или MAX.',
    breadcrumbs: [home, { name: 'Конкурс', path: '/rozygrysh' }], sitemap: { priority: 0.7, changefreq: 'weekly' },
  },
  {
    path: '/rozygrysh/pravila', module: 'src/app/pages/GiveawayRulesPage.tsx', kind: 'page',
    title: 'Правила конкурса с поездкой на караоке-трамвае — «Изнанка»',
    description: 'Правила конкурса «Изнанки»: организатор, сроки, условие подписки на канал в Telegram или MAX, приз, случайный выбор победителя, уведомление, налоги и обработка персональных данных.',
    breadcrumbs: [home, { name: 'Конкурс', path: '/rozygrysh' }, { name: 'Правила', path: '/rozygrysh/pravila' }], sitemap: { priority: 0.3, changefreq: 'monthly' },
  },
  {
    path: '/privacy', kind: 'legal', title: 'Политика обработки персональных данных — «Изнанка»',
    description: 'Как агентство экскурсий «Изнанка» обрабатывает данные заявителей, как проходит фото- и видеосъёмка, какие cookie и аналитику использует сайт и как отозвать согласие.',
    breadcrumbs: [home, { name: 'Политика обработки данных', path: '/privacy' }], sitemap: { priority: 0.2, changefreq: 'yearly' },
  },
  {
    path: '/consent', kind: 'legal', title: 'Согласие на обработку персональных данных — «Изнанка»',
    description: 'Отдельное согласие на обработку данных заявки на экскурсию: цели, состав данных, срок и порядок отзыва.',
    breadcrumbs: [home, { name: 'Согласие на обработку данных', path: '/consent' }], sitemap: { priority: 0.2, changefreq: 'yearly' },
  },
  {
    path: '/media-consent', kind: 'legal', title: 'Согласие на фото и видео — «Изнанка»',
    description: 'Условия съёмки и публикации изображений участников экскурсий, полномочия представителей, отзыв согласия и бланк в формате Word.',
    breadcrumbs: [home, { name: 'Согласие на фото и видео', path: '/media-consent' }], sitemap: { priority: 0.2, changefreq: 'yearly' },
  },
  {
    path: '/terms', kind: 'legal', title: 'Условия заявки и участия в экскурсии — «Изнанка»',
    description: 'Как согласуется экскурсия, правила безопасности, участие детей и групп. Заявка не является подтверждённым оплаченным заказом.',
    breadcrumbs: [home, { name: 'Условия участия', path: '/terms' }], sitemap: { priority: 0.2, changefreq: 'yearly' },
  },
];

export const notFoundSeo: SeoRoute = {
  path: '/404', kind: 'error', noindex: true, breadcrumbs: [],
  title: 'Страница не найдена — «Изнанка»',
  description: 'Такой страницы нет. Перейдите в каталог экскурсий «Изнанки» в Краснодаре.',
};

export function seoForPath(pathname: string): SeoRoute | undefined {
  return seoRoutes.find((route) => route.path === pathname);
}

export function socialImage(route: SeoRoute) {
  const media = mediaManifest[route.image ?? 'IMG_0129.JPG'];
  const [url, width] = media.sources.jpeg.at(-1)!.split(' ');
  const pixels = Number.parseInt(width, 10);
  return { path: url, width: pixels, height: Math.round(pixels * media.height / media.width) };
}

/** AVIF candidates (the `<picture>`'s first source) of a `preloadHero` route's image for `<link rel="preload">`. */
export function heroPreload(route: SeoRoute): { href: string; srcset: string; sizes: string } | undefined {
  if (!route.preloadHero || !route.image) return undefined;
  const sources = mediaManifest[route.image].sources.avif;
  return { href: sources.at(-1)!.split(' ')[0], srcset: sources.join(', '), sizes: route.preloadHero };
}

/** schema.org graph for a route; `origin` has no trailing slash; `facts` supply meeting places. */
export function structuredData(route: SeoRoute, origin: string, facts?: ExcursionFactsMap): Record<string, unknown> {
  const organizationId = `${origin}/#organization`;
  const url = `${origin}${route.path}`;
  const image = `${origin}${socialImage(route).path}`;
  const address = { '@type': 'PostalAddress', addressLocality: site.city, addressRegion: site.region, addressCountry: 'RU' };
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'TravelAgency', '@id': organizationId, name: site.name, legalName: site.legalName, url: `${origin}/`,
      logo: `${origin}/icon-512.png`, image, telephone: site.phone, address, areaServed: { '@type': 'City', name: site.city }, sameAs: site.sameAs,
    },
    ...(route.path === '/osnovatel' ? [{
      '@type': 'Person', '@id': `${origin}/osnovatel#person`, name: 'Константин Постовой', jobTitle: 'Основатель, экскурсовод',
      url, worksFor: { '@id': organizationId },
      knowsAbout: ['Экскурсии', 'Краеведение Кубани', 'Профориентация школьников', 'Агротуризм'],
    }] : []),
    { '@type': 'WebSite', '@id': `${origin}/#website`, url: `${origin}/`, name: site.name, inLanguage: 'ru-RU', publisher: { '@id': organizationId } },
    {
      '@type': 'WebPage', '@id': `${url}#webpage`, url, name: route.title, description: route.description, inLanguage: 'ru-RU',
      isPartOf: { '@id': `${origin}/#website` }, primaryImageOfPage: image,
      ...(route.breadcrumbs.length > 1 ? { breadcrumb: { '@id': `${url}#breadcrumbs` } } : {}),
    },
  ];
  if (route.breadcrumbs.length > 1) {
    graph.push({
      '@type': 'BreadcrumbList', '@id': `${url}#breadcrumbs`,
      itemListElement: route.breadcrumbs.map((crumb, index) => ({ '@type': 'ListItem', position: index + 1, name: crumb.name, item: `${origin}${crumb.path}` })),
    });
  }
  const provider = { '@id': organizationId };
  if (route.trip) {
    const place = facts?.[route.trip.excursion];
    graph.push({
      '@type': 'TouristTrip', name: route.trip.name, description: route.description, url, image, provider, inLanguage: 'ru',
      touristType: ['Школьные классы', 'Студенты', 'Коллеги и компании', 'Семьи'],
      ...(place?.address ? { itinerary: { '@type': 'Place', name: place.meetingPoint || route.trip.name, address: { ...address, streetAddress: place.address } } } : {}),
    });
  }
  if (route.path === '/' || route.path === '/excursions/kttu') {
    const tours = orderByExcursion(route.path === '/' ? [...kttuTours, botanicalTour] : kttuTours, facts, (tour) => tour.id).filter((tour) => !isExcursionHidden(facts, tour.id));
    graph.push({
      '@type': 'ItemList', name: route.path === '/' ? 'Экскурсии «Изнанки»' : 'Экскурсии МУП «КТТУ»',
      itemListElement: tours.map((tour, index) => ({
        '@type': 'ListItem', position: index + 1,
        item: { '@type': 'TouristTrip', name: tour.title, description: tour.text, provider, url: `${origin}${tour.route}` },
      })),
    });
  }
  if (route.path === '/kursy') {
    const school = { '@type': 'EducationalOrganization', name: coursePartner.name, url: coursePartner.url };
    graph.push({
      '@type': 'ItemList', name: 'Курсы и лекции школы «Добро и Небо»',
      itemListElement: courseValues.map((course, index) => ({
        '@type': 'ListItem', position: index + 1,
        item: { '@type': 'Course', name: courses[course].title, description: courses[course].lead, provider: school, url: `${url}#${course}`, inLanguage: 'ru' },
      })),
    });
  }
  const faq = faqByPath[route.path];
  if (faq?.length) {
    graph.push({
      '@type': 'FAQPage', '@id': `${url}#faq`,
      mainEntity: faq.map((item) => ({ '@type': 'Question', name: item.question, acceptedAnswer: { '@type': 'Answer', text: item.answer } })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

/** sitemap.xml served per request (server/app.ts) so excursions hidden by the admin drop out at once. */
export function sitemapXml(origin: string, lastmod: string, facts?: ExcursionFactsMap): string {
  const entries = seoRoutes.filter((route) => route.sitemap && !route.noindex && !(route.trip && isExcursionHidden(facts, route.trip.excursion))).map((route) => {
    const image = socialImage(route);
    return `  <url>\n    <loc>${origin}${route.path}</loc>\n    <lastmod>${lastmod}</lastmod>\n    <changefreq>${route.sitemap!.changefreq}</changefreq>\n    <priority>${route.sitemap!.priority.toFixed(1)}</priority>\n    <image:image><image:loc>${origin}${image.path}</image:loc></image:image>\n  </url>`;
  });
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">\n${entries.join('\n')}\n</urlset>\n`;
}
